/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { useEffect } from 'react';

import { IMfrmChooseYourFoundationData } from './mfrm-choose-your-foundation.data';
import { IMfrmChooseYourFoundationProps } from './mfrm-choose-your-foundation.props.autogenerated';
import fetchChooseYourFoundationDatabaseDataAction, {
    FetchChooseYourFoundationDatabaseInput,
    IDatabaseRow
} from '../../actions/fetch-choose-your-foundation-database.action';
import { getActivePricesAsync, getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getByIdsAsync, searchByCriteriaAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductSearchCriteria, ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getBVAttributeValues } from '../../Utilities/get-bv-attributes';
import { generateImageUrl } from '../../data-actions/utilities/utils';
import { SelectYourFoundationInput } from '../../actions/select-your-foundation.action';
import _getInventoryQuantity from '../../Utilities/get-inventory-quantity';
import { _smallParcelProductATPBooleanStatus } from '../../Utilities/mfrm-buybox-utils';

export interface AdjustableBase {
    eyebrowText?: string;
    productName: string;
    rating: number;
    numberOfRatings: number;
    standardPrice: number;
    currentPrice?: number;
    imgSrc: string;
    imgAlt: string;
}

export interface MergedBase extends SimpleProduct {
    outOfStock?: boolean;
    productType?: string;
    CustomerContextualPrice: number | undefined;
    DiscountAmount: number | undefined;
    TotalRatings: number | undefined;
    AverageRating: number | undefined;
    isRecommended: boolean;
    brand?: string;
}

export type ChooseYourFoundationBaseId = number | 'Mattress Only';
export type Tab = 'All' | 'Adjustable Bases' | 'Box Springs';
export interface IMfrmChooseYourFoundationViewProps extends IMfrmChooseYourFoundationProps<IMfrmChooseYourFoundationData> {
    onClickCard(key: ChooseYourFoundationBaseId, index: number): void;
    onClickTab(tab: Tab): void;
    product: SimpleProduct;
    bases: MergedBase[];
    splitBases: MergedBase[];
    selectedBaseId: ChooseYourFoundationBaseId;
    selectedTab: Tab;
    isLoading: boolean;
}

export interface AdjustableBaseObject {
    foundation?: MergedBase;
    listPosition?: number;
}

/**
 *
 * MfrmChooseYourFoundation component
 * @extends {React.PureComponent<IMfrmChooseYourFoundationProps<IMfrmChooseYourFoundationData>>}
 */

export const MfrmChooseYourFoundation: React.FC<IMfrmChooseYourFoundationProps<IMfrmChooseYourFoundationData>> = props => {

    const {
        data: {
            product: { result: productResult },
            productCompareBuyboxStateSharing: { result: buyboxState }
        }
    } = props;

    const [database, setDatabase] = React.useState<IDatabaseRow[] | null>(null);
    const [bases, setBases] = React.useState<MergedBase[]>([]);
    const [splitBases, setSplitBases] = React.useState<MergedBase[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [selectedBaseId, setSelectedBaseId] = React.useState<ChooseYourFoundationBaseId>('Mattress Only');
    const [selectedTab, setSelectedTab] = React.useState<Tab>('All');
    const hasSelectedBase = React.useRef<boolean>(false);

    const fetchProductTypeAndOutOfStockStatus = async (baseId: number, itemId: string): Promise<{ productType?: string, outOfStock: boolean }> => {
        const attributeValues = await getAttributeValuesAsync(
            {
                callerContext: props.context.actionContext,
                queryResultSettings: {}
            },
            baseId,
            props.context.actionContext.requestContext.apiSettings.channelId,
            props.context.actionContext.requestContext.apiSettings.catalogId
        );

        //TODO come back and update when we clean up IS/OOS logic
        const OOS = attributeValues
            ?.find(attribute => attribute.Name === 'EcomAllVariantOOSInfo1')
            ?.TextValue?.split(",")
            .find(item => item.includes(`${baseId}|1`)) || attributeValues?.find(attribute => attribute.Name === 'Out of Stock')?.TextValue;

        const textValue = attributeValues?.find(attribute => attribute.Name === 'Product Type')?.TextValue;
        const parcelShippingMethod = attributeValues?.find(attribute => attribute.Name === 'shippingInformation')?.TextValue === "Parcel";
        const parcelOOS = parcelShippingMethod && await _smallParcelProductATPBooleanStatus(baseId, itemId, props.context.app.config.weeksPDPforATPCall, props.context);
        if (typeof textValue === 'string') {
            return parcelShippingMethod ? ( { productType: textValue, outOfStock: !!parcelOOS } ) : ( { productType: textValue, outOfStock: !!OOS });
        } else {
            return parcelShippingMethod ? ( { outOfStock: !!parcelOOS } ) : ( { outOfStock: !!OOS });
        }
    };

    const _parseValue = (productRating?: string | number): number => {
        const value = +(productRating || 0);
        return Math.round(value * 10) / 10;
    };

    const getBasesFromProductId = async (RecordId: number, baseType: string, quantity: number) => {
        const databaseRow: IDatabaseRow | undefined = database!.find((item: IDatabaseRow) => item.mattressVariantReqId === `${RecordId}`);
        if (!databaseRow || !databaseRow[baseType]) {
            baseType === 'baseVariantReqIdList' ? setBases([]) : setSplitBases([]);
            setIsLoading(false);
        } else {
            try {
                const bases = await getByIdsAsync(
                    { callerContext: props.context.actionContext },
                    props.context.request.apiSettings.channelId,
                    databaseRow[baseType].map((id: string) => {
                        return Number(id);
                    })
                );
                const basePrices = await getActivePricesAsync(
                    { callerContext: props.context.actionContext },
                    {
                        ChannelId: props.context.actionContext.requestContext.apiSettings.channelId,
                        CatalogId: props.context.actionContext.requestContext.apiSettings.catalogId
                    },
                    databaseRow[baseType].map((id: string) => {
                        return Number(id);
                    }),
                    new Date(),
                    null,
                    [],
                    true
                );
                let productSearchResult: ProductSearchResult[] = [];
                if (bases.length) {
                    const productSearchCriteria: ProductSearchCriteria = {
                        Ids: [...new Set(bases.map(item => item.MasterProductId ?? 0))],
                        Context: {
                            ChannelId: +props.context.actionContext.requestContext.apiSettings.channelId,
                            CatalogId: +props.context.actionContext.requestContext.apiSettings.catalogId
                        },
                        IncludeAttributes: true,
                        SearchCondition: '*'
                    };
                    productSearchResult = await searchByCriteriaAsync({ callerContext: props.context.actionContext }, productSearchCriteria);
                }

                const mergedBases = bases.map((base, i) => {
                    const baseImageUrl = generateImageUrl(base.PrimaryImageUrl, props.context.request.apiSettings);
                    return {
                        ...base,
                        CustomerContextualPrice: basePrices[i].CustomerContextualPrice! * quantity,
                        DiscountAmount: basePrices[i].DiscountAmount! * quantity,
                        AverageRating:
                            getBVAttributeValues(
                                productSearchResult.find(item => item.MasterProductId === base.MasterProductId)?.AttributeValues,
                                'BVAverageOverallRating'
                            ) ?? _parseValue(productSearchResult.find(item => item.MasterProductId === base.MasterProductId)?.AverageRating),
                        TotalRatings:
                            getBVAttributeValues(
                                productSearchResult.find(item => item.MasterProductId === base.MasterProductId)?.AttributeValues,
                                'BVTotalReviewCount'
                            ) ?? _parseValue(productSearchResult.find(item => item.MasterProductId === base.MasterProductId)?.TotalRatings),
                        PrimaryImageUrl: baseImageUrl ? baseImageUrl : '',
                        brand: productSearchResult.find(item => item.MasterProductId === base.MasterProductId)?.AttributeValues?.find(attr => attr.KeyName?.trim() === 'VendorBrand')?.TextValue,
                        isRecommended: base.RecordId.toString() === databaseRow.recommendedBaseVariantReqId,
                        quantity: quantity
                    };
                });

                baseType === 'baseVariantReqIdList' ? setBases(mergedBases) : setSplitBases(mergedBases);
                setIsLoading(false);
                mergedBases.forEach((item, index) => {
                    const itemId = item.ItemId || '';
                    fetchProductTypeAndOutOfStockStatus(Number(databaseRow[baseType][index]), itemId)
                        .then(({ productType, outOfStock }) => {
                            baseType === 'baseVariantReqIdList' ? setBases(bases => {
                                bases[index].productType = productType;
                                bases[index].outOfStock = outOfStock;
                                return bases;
                            }) : setSplitBases(bases => {
                                bases[index].productType = productType;
                                bases[index].outOfStock = outOfStock;
                                return bases;
                            });
                        })
                        .catch(e => console.error(e));
                });
            } catch (error) {
                setIsLoading(false);
                console.error(error);
            }

        }
        props.context.actionContext.update(new SelectYourFoundationInput(null), { foundation: null });
    };


    const handleClickCard = (key: ChooseYourFoundationBaseId, index: number) => {
        if (key === 'Mattress Only') {
            if (selectedBaseId !== 'Mattress Only') {
                const base = bases.find(base => base.RecordId === selectedBaseId) ? bases.find(base => base.RecordId === selectedBaseId)! : splitBases.find(splitBase => splitBase.RecordId === selectedBaseId)!;
                // @ts-ignore
                utag.link({
                    enhanced_action: 'remove_base',
                    event: 'PDP-card-action-remove-base',
                    event_category: 'PDP Card Action',
                    event_action: 'Remove Base/Foundation',
                    event_label: `${base.ItemId}: ${base.Name}`,
                    event_noninteraction: 'false'
                });
            }
            setSelectedBaseId('Mattress Only');
            props.context.actionContext.update(new SelectYourFoundationInput(null), { foundation: null });
        } else {
            const base = bases.find(base => base.RecordId === key) ? bases.find(base => base.RecordId === key)! : splitBases.find(splitBase => splitBase.RecordId === key)!;
            if (selectedBaseId === base.RecordId) {
                setSelectedBaseId('Mattress Only');
                props.context.actionContext.update(new SelectYourFoundationInput(null), { foundation: null });
            } else {
                if (!hasSelectedBase.current) {
                    hasSelectedBase.current = true;
                    // @ts-ignore
                    utag.link({
                        enhanced_action: 'add_base',
                        event: 'PDP-card-action-add-base',
                        event_category: 'PDP Card Action',
                        event_action: 'Add Base/Foundation',
                        event_label: `${base.ItemId}: ${base.Name}`,
                        event_noninteraction: 'false'
                    });
                } else {
                    // @ts-ignore
                    utag.link({
                        enhanced_action: 'change_base',
                        event: 'PDP-card-action-change-base',
                        event_category: 'PDP Card Action',
                        event_action: 'Change Base/Foundation',
                        event_label: `${base.ItemId}: ${base.Name}`,
                        event_noninteraction: 'false'
                    });
                }
                setSelectedBaseId(base.RecordId);
                props.context.actionContext.update(new SelectYourFoundationInput(base), { foundation: base, listPosition: index });
            }
        }
    };

    const handleClickTab = (tab: Tab) => {
        setSelectedTab(tab);
    };

    useEffect(() => {
        const actionInput = new FetchChooseYourFoundationDatabaseInput(props.context.app.config.jsonSrc);
        fetchChooseYourFoundationDatabaseDataAction(actionInput, props.context.actionContext)
            .then(database => setDatabase(database))
            .catch(e => {
                console.error('Error fetching database', e);
            });
    }, []);

    // useEffect(() => {
    //     const cookies = new Cookies();
    //     const currentZipCode = cookies.get('zip_code');
    //     setZipCode(currentZipCode);
    // }, [buyboxState?.zipCode]);

    useEffect(() => {
        setSelectedBaseId('Mattress Only');
        setSelectedTab('All');
        database && productResult?.RecordId && getBasesFromProductId(productResult.RecordId, 'splitVariantReqIdList', 2);
        database && productResult?.RecordId && getBasesFromProductId(productResult.RecordId, 'baseVariantReqIdList', 1);
    }, [productResult?.RecordId, database !== null, buyboxState?.zipCode]);

    return props.renderView({
        ...props,
        product: productResult,
        bases,
        splitBases,
        selectedBaseId,
        selectedTab,
        isLoading,
        onClickCard: handleClickCard,
        onClickTab: handleClickTab
    });
};

export default MfrmChooseYourFoundation;
